import { omit } from "lodash";

import {
  CreateSavingsPayload,
  SavingDetails,
  UpdateSavingsPayload,
} from "../../pages/Savings";
import { WindowType } from "../../utils";
import { baseApi } from "../api";
import { SavingsHistoryResponse, SavingsResponse } from "./types";

interface GetSavingsArgs {
  activeOnly?: boolean;
  year?: number;
}

interface GetSavingArgs {
  id: number;
  window: WindowType;
}

interface UpdateSavingArgs {
  id: number;
  payload: UpdateSavingsPayload;
}

interface UpdateSavingStatusArgs {
  id: number;
  isActive: boolean;
}

const savingsListTags = (result?: SavingsResponse) => [
  { type: "Saving" as const, id: "LIST" },
  ...(result?.savings.map((saving) => ({
    type: "Saving" as const,
    id: saving.id,
  })) ?? []),
];

const invalidateSaving = (id: number) => [
  { type: "Account" as const, id: "LIST" },
  { type: "Saving" as const, id },
  { type: "Saving" as const, id: "LIST" },
  "SavingHistory" as const,
  "SavingYears" as const,
  "Finances" as const,
];

export const savingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSavings: builder.query<SavingsResponse, GetSavingsArgs | void>({
      query: (args) => {
        const { activeOnly = true, year } = args ?? {};
        return {
          url: "/savings",
          params: year === undefined ? { activeOnly } : { activeOnly, year },
        };
      },
      providesTags: savingsListTags,
    }),

    getSavingsHistory: builder.query<
      SavingsHistoryResponse,
      GetSavingsArgs | void
    >({
      query: (args) => {
        const { activeOnly = true, year } = args ?? {};
        return {
          url: "/savings/history",
          params: year === undefined ? { activeOnly } : { activeOnly, year },
        };
      },
      providesTags: ["SavingHistory"],
    }),

    getSaving: builder.query<SavingDetails, GetSavingArgs>({
      query: ({ id, window }) => ({
        url: "/savings/" + id,
        params: { window },
      }),
      providesTags: (_result, _error, { id }) => [{ type: "Saving", id }],
    }),

    getSavingsYears: builder.query<number[], boolean | void>({
      query: (activeOnly = true) => ({
        url: "/savings/years",
        params: { activeOnly },
      }),
      providesTags: ["SavingYears"],
      keepUnusedDataFor: 300,
    }),

    createSavings: builder.mutation<unknown, CreateSavingsPayload>({
      query: (payload) => ({
        url: "/savings",
        method: "post",
        data: payload,
      }),
      invalidatesTags: [
        { type: "Account", id: "LIST" },
        { type: "Saving", id: "LIST" },
        "SavingHistory",
        "SavingYears",
        "Finances",
      ],
    }),

    updateSaving: builder.mutation<void, UpdateSavingArgs>({
      query: ({ id, payload }) => ({
        url: "/savings/" + id,
        method: "patch",
        data: omit(payload, "id"),
      }),
      invalidatesTags: (_result, _error, { id }) => invalidateSaving(id),
    }),

    updateSavingAccountStatus: builder.mutation<void, UpdateSavingStatusArgs>({
      query: ({ id, isActive }) => ({
        url: "/savings/" + id,
        method: "patch",
        data: { isActive },
      }),
      invalidatesTags: (_result, _error, { id }) => invalidateSaving(id),
    }),

    deleteSaving: builder.mutation<void, number>({
      query: (id) => ({
        url: "/savings/" + id,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, id) => invalidateSaving(id),
    }),
  }),
});

export const {
  useGetSavingsQuery,
  useGetSavingsHistoryQuery,
  useGetSavingQuery,
  useGetSavingsYearsQuery,
  useCreateSavingsMutation,
  useUpdateSavingMutation,
  useUpdateSavingAccountStatusMutation,
  useDeleteSavingMutation,
} = savingsApi;
